import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { buildLabel } from '../utils/labelBuilder';
import { groupPresetsByFamily } from '../utils/orderUtils';

const { FiPlus, FiMinus, FiCoffee, FiChevronDown, FiChevronRight } = FiIcons;

const QuickAddGrid = ({ presets, onAdd, orders, expandedFamilies, onToggleFamily }) => {
  const groupedPresets = groupPresetsByFamily(presets);

  const getFamilyIcon = (family) => {
    const icons = {
      kopi: FiCoffee,
      teh: FiCoffee,
      cham: FiCoffee,
      milo: FiCoffee,
      barley: FiCoffee,
      bandung: FiCoffee,
      'lemon-tea': FiCoffee,
      'lime-juice': FiCoffee,
      '100-plus': FiCoffee,
      coke: FiCoffee,
      sprite: FiCoffee,
      water: FiCoffee,
      chrysanthemum: FiCoffee
    };
    return icons[family] || FiCoffee;
  };

  const getFamilyColor = (family) => {
    const colors = {
      kopi: 'from-amber-400 to-orange-500',
      teh: 'from-green-400 to-emerald-500',
      cham: 'from-purple-400 to-pink-500',
      milo: 'from-yellow-400 to-amber-500',
      barley: 'from-blue-400 to-cyan-500',
      bandung: 'from-pink-400 to-rose-500',
      'lemon-tea': 'from-lime-400 to-green-500',
      'lime-juice': 'from-lime-300 to-green-400',
      '100-plus': 'from-orange-400 to-red-500',
      coke: 'from-gray-700 to-gray-900',
      sprite: 'from-green-300 to-lime-400',
      water: 'from-blue-300 to-blue-500',
      chrysanthemum: 'from-yellow-300 to-orange-400'
    };
    return colors[family] || 'from-gray-400 to-gray-500';
  };

  const getFamilyTotalCount = (familyPresets) => {
    return familyPresets.reduce((total, preset) => total + (orders[preset.key] || 0), 0);
  };

  const QuickAddCard = ({ preset }) => {
    const count = orders[preset.key] || 0;
    const label = buildLabel(preset.key);

    const handleDecrease = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (count > 0) {
        onAdd(preset.key, -1);
      }
    };

    const handleIncrease = (e) => {
      e.preventDefault();
      e.stopPropagation();
      onAdd(preset.key, 1);
    };

    return (
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ scale: 1.02, y: -2 }}
        className="bg-white/60 backdrop-blur-sm rounded-xl shadow-lg border border-white/30 overflow-hidden hover:shadow-xl transition-all duration-300"
      >
        <div className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="font-semibold text-gray-800 text-sm leading-tight">
                {label}
              </h4>
              {count > 0 && (
                <div className="flex items-center space-x-2 mt-2">
                  <span className="text-lg font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                    {count}
                  </span>
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={handleDecrease}
                    className="p-1 rounded-lg bg-gradient-to-br from-red-50 to-red-100 text-red-600 hover:from-red-100 hover:to-red-200 transition-all duration-200 min-w-[28px] min-h-[28px] flex items-center justify-center shadow-md hover:shadow-lg"
                    aria-label={`Remove one ${label}`}
                  >
                    <SafeIcon icon={FiMinus} className="text-xs" />
                  </motion.button>
                </div>
              )}
            </div>
            
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={handleIncrease}
              className="w-8 h-8 bg-gradient-to-br from-green-50 to-green-100 rounded-lg flex items-center justify-center shadow-md hover:shadow-lg text-green-600 hover:from-green-100 hover:to-green-200 transition-all duration-200"
              aria-label={`Add ${label}`}
            >
              <SafeIcon icon={FiPlus} className="text-sm" />
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <motion.section 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div className="flex items-center space-x-3">
        <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg flex items-center justify-center">
          <SafeIcon icon={FiCoffee} className="text-white text-sm" />
        </div>
        <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
          Menu
        </h2>
      </div>
      
      <div className="space-y-3">
        {Object.entries(groupedPresets).map(([family, familyPresets]) => {
          const isExpanded = expandedFamilies.includes(family);
          const totalCount = getFamilyTotalCount(familyPresets);
          const FamilyIcon = getFamilyIcon(family);
          const familyColor = getFamilyColor(family);
          
          return (
            <motion.div
              key={family}
              layout
              className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden"
            >
              {/* Family Header */}
              <motion.button
                onClick={() => onToggleFamily(family)}
                className="w-full p-4 flex items-center justify-between hover:bg-white/50 transition-all duration-200"
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 bg-gradient-to-br ${familyColor} rounded-xl flex items-center justify-center shadow-lg`}>
                    <SafeIcon icon={FamilyIcon} className="text-white text-lg" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-lg font-bold text-gray-800 capitalize">
                      {family.replace('-', ' ')}
                    </h3>
                    {totalCount > 0 && (
                      <p className="text-sm text-amber-600 font-semibold">
                        {totalCount} item{totalCount !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {totalCount > 0 && (
                    <div className="bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 px-3 py-1 rounded-full text-sm font-bold">
                      {totalCount}
                    </div>
                  )}
                  <SafeIcon 
                    icon={isExpanded ? FiChevronDown : FiChevronRight} 
                    className="text-gray-500 text-lg" 
                  />
                </div>
              </motion.button>

              {/* Family Items */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="p-4 pt-0 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {familyPresets.map((preset, index) => (
                        <motion.div
                          key={preset.key}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <QuickAddCard preset={preset} />
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </motion.section>
  );
};

export default QuickAddGrid;